import { useEffect, useRef, useState } from 'react';
import { Users, BookOpen, UserCheck, Eye, Mail, Heart, TrendingUp, ArrowRight, Zap } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';

/* ── Count-up hook ── */
function useCountUp(end: number, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!startOnView) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const tick = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration, startOnView]);

  return { count, ref };
}

/* ── Stat card data ── */
const stats = [
  { icon: Users, value: 2700000, suffix: '+', label: 'Active Readers', format: '2.7M' },
  { icon: BookOpen, value: 5200, suffix: '+', label: 'Books Published', format: '5,200' },
  { icon: UserCheck, value: 3800, suffix: '+', label: 'Authors Onboarded', format: '3,800' },
  { icon: Eye, value: 4100000, suffix: '+', label: 'Monthly Book Views', format: '4.1M' },
  { icon: Mail, value: 890000, suffix: '+', label: 'Email Campaign Reach', format: '890K' },
  { icon: Heart, value: 72, suffix: '%', label: 'Reader Engagement Rate', format: '72' },
];

/* ── Impact highlights ── */
const highlights = [
  { icon: TrendingUp, text: 'Up to 3× increase in book visibility within weeks' },
  { icon: Zap, text: 'Featured books receive significantly higher engagement' },
  { icon: Mail, text: 'Email campaigns consistently drive reader traffic' },
];

/* ── Growth chart data ── */
const growthData = [
  { month: 'Jan', readers: 120, exposure: 80 },
  { month: 'Feb', readers: 210, exposure: 130 },
  { month: 'Mar', readers: 380, exposure: 220 },
  { month: 'Apr', readers: 520, exposure: 340 },
  { month: 'May', readers: 710, exposure: 490 },
  { month: 'Jun', readers: 980, exposure: 650 },
  { month: 'Jul', readers: 1250, exposure: 870 },
  { month: 'Aug', readers: 1600, exposure: 1100 },
  { month: 'Sep', readers: 2050, exposure: 1400 },
  { month: 'Oct', readers: 2500, exposure: 1750 },
  { month: 'Nov', readers: 3100, exposure: 2200 },
  { month: 'Dec', readers: 3800, exposure: 2800 },
];

/* ── Single stat card component ── */
function StatCard({ icon: Icon, format, suffix, label, delay }: {
  icon: typeof Users;
  format: string;
  suffix: string;
  label: string;
  delay: number;
}) {
  const numericEnd = parseFloat(format.replace(/[^0-9.]/g, ''));
  const prefix = format.replace(/[0-9.,]/g, '');
  const { count, ref } = useCountUp(Math.round(numericEnd * 10), 2200);

  const displayed = (count / 10).toFixed(
    format.includes('.') ? 1 : 0,
  );

  return (
    <div
      ref={ref}
      className="bg-card border border-border rounded-xl p-5 lg:p-6 text-center hover:border-primary/30 transition-all duration-500 animate-fade-in group"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
        <Icon className="text-primary" size={22} />
      </div>
      <p className="text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight">
        {prefix}{displayed}{suffix}
      </p>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

/* ── Main section ── */
export function AnalyticsSocialProof() {
  return (
    <>
      {/* ── Headline ── */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-primary/30 blur-[140px]" />
        </div>

        <div className="container-library relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <span className="brand-text block mb-3 animate-fade-in">Results</span>
            <h2
              className="heading-display text-foreground max-w-3xl mx-auto animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              Trusted by Authors.{' '}
              <span className="text-primary">Proven by Results.</span>
            </h2>
            <p
              className="text-body text-lg mt-6 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              Join a platform that connects your book to over 1.7 million active
              readers and delivers measurable growth.
            </p>
          </div>

          {/* ── Stat cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
            {stats.map((s, i) => (
              <StatCard key={s.label} {...s} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── Performance highlights ── */}
      <section className="section-padding bg-card/30">
        <div className="container-library">
          <div className="text-center mb-10">
            <span className="brand-text block mb-3">Impact</span>
            <h2 className="heading-section text-foreground">
              Performance That Speaks for Itself
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center bg-card border border-border rounded-xl p-6 lg:p-8 hover:border-primary/30 transition-all duration-500 animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <h.icon className="text-primary" size={22} />
                </div>
                <p className="text-secondary-foreground text-sm leading-relaxed font-medium">
                  {h.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── Case study + Growth chart ── */}
      <section className="section-padding">
        <div className="container-library">
          <div className="text-center mb-12">
            <span className="brand-text block mb-3">Transformation</span>
            <h2 className="heading-section text-foreground">
              From Invisible to Unmissable
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Case study card */}
            <div className="bg-card border border-border rounded-xl p-6 lg:p-8 animate-fade-in">
              <h3 className="heading-card text-foreground mb-6">
                Before &amp; After Joining
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Before */}
                <div className="space-y-3">
                  <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Before</span>
                  {['Low visibility', 'Limited readership', 'Minimal engagement'].map((t) => (
                    <div key={t} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                      <span className="text-sm text-muted-foreground">{t}</span>
                    </div>
                  ))}
                </div>

                {/* After */}
                <div className="space-y-3">
                  <span className="text-xs font-semibold tracking-widest uppercase text-primary">After</span>
                  {['Thousands of views', 'Increased engagement', 'Strong reader interaction'].map((t) => (
                    <div key={t} className="flex items-start gap-2">
                      <ArrowRight className="mt-0.5 text-primary shrink-0" size={14} />
                      <span className="text-sm text-foreground font-medium">{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-xs text-muted-foreground italic">
                  Results based on aggregated author performance data across the platform.
                </p>
              </div>
            </div>

            {/* Growth chart */}
            <div className="bg-card border border-border rounded-xl p-6 lg:p-8 animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <h3 className="heading-card text-foreground mb-2">Growth Over Time</h3>
              <p className="text-small mb-6">Average reader & exposure trajectory after listing</p>

              <div className="h-56 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradReaders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(38 92% 50%)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(38 92% 50%)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradExposure" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(0 0% 55%)" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="hsl(0 0% 55%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(0 0% 55%)' }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(0 0% 55%)' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(0 0% 7%)',
                        border: '1px solid hsl(0 0% 18%)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: 'hsl(0 0% 95%)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="readers"
                      stroke="hsl(38 92% 50%)"
                      strokeWidth={2}
                      fill="url(#gradReaders)"
                      name="Readers"
                    />
                    <Area
                      type="monotone"
                      dataKey="exposure"
                      stroke="hsl(0 0% 55%)"
                      strokeWidth={1.5}
                      fill="url(#gradExposure)"
                      name="Exposure"
                      strokeDasharray="4 4"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex gap-6 mt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-3 h-0.5 rounded bg-primary" /> Readers
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-3 h-0.5 rounded bg-muted-foreground" style={{ borderTop: '1px dashed' }} /> Exposure
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── Trust statement ── */}
      <section className="py-12 md:py-16">
        <div className="container-library text-center">
          <p className="text-body text-lg max-w-2xl mx-auto italic animate-fade-in">
            "Join a growing network of authors leveraging data-driven exposure
            to reach <span className="text-primary font-medium">real readers</span>."
          </p>
        </div>
      </section>
    </>
  );
}
